import { isKeyboardEditor, keyboardViewport } from './keyboard-geometry';

/** App-owned residual reveal only. Native focus/IME panning is never prevented.
 * 408ms is our accepted UI policy, not an OS keyboard animation clock. */
export function watchFocusedFieldScroll(): () => void {
  let frame = 0;
  let editor: HTMLElement | null = null;
  let deadline = 0;
  let lastY = scrollY;
  let lastTop = 0;
  let lastBottom = 0;
  let quietSince = 0;
  let start = 0;
  let from = 0;
  let to = 0;
  let expectedY = scrollY;
  let stoppedByGesture = false;
  let tap: { target: HTMLElement; id: number; x: number; y: number; up: boolean } | null = null;
  let completedTap: HTMLElement | null = null;
  const cancel = () => { cancelAnimationFrame(frame); frame = 0; start = 0; };
  const eligible = (e: Element | null): e is HTMLElement => isKeyboardEditor(e) &&
    !!e.closest('[data-keyboard-surface]') && !e.closest('.sheet-layer, .sheet-interactive');
  const delta = () => {
    if (!editor) return 0;
    const v = keyboardViewport(), r = editor.getBoundingClientRect();
    return r.top < v.top + 12 ? r.top - v.top - 12 :
      r.bottom > v.bottom - 12 ? Math.min(r.bottom - v.bottom + 12, r.top - v.top - 12) : 0;
  };
  const tick = (now: number) => {
    frame = 0;
    if (!editor?.isConnected || document.activeElement !== editor || stoppedByGesture ||
      document.hidden || document.body.style.overflow === 'hidden' || now > deadline) return;
    const v = keyboardViewport();
    const geometryMoved = Math.abs(v.top - lastTop) > 0.5 || Math.abs(v.bottom - lastBottom) > 0.5;
    const foreignScroll = Math.abs(scrollY - (start ? expectedY : lastY)) > 1;
    if (geometryMoved || foreignScroll) { start = 0; quietSince = now; }
    lastTop = v.top; lastBottom = v.bottom; lastY = scrollY;
    if (!start && now - quietSince >= 80) {
      const d = delta();
      if (Math.abs(d) < 1) return;
      from = scrollY;
      to = Math.max(0, Math.min(document.documentElement.scrollHeight - innerHeight, from + d));
      if (Math.abs(to - from) < 1) return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        scrollTo({ top: to, behavior: 'instant' }); return;
      }
      start = now;
    }
    if (start) {
      const progress = Math.min(1, (now - start) / 408);
      // Quintic smoothstep: zero velocity and acceleration at both endpoints.
      // Keep the focused-field policy at 408ms; the sheet controller is separate.
      const eased = progress * progress * progress * (10 + progress * (-15 + 6 * progress));
      scrollTo({ top: from + (to - from) * eased, behavior: 'instant' });
      expectedY = scrollY; lastY = scrollY;
      if (progress === 1) return;
    }
    frame = requestAnimationFrame(tick);
  };
  const begin = () => {
    // A retained editor emits no focusin on re-tap. Consume only a completed
    // trusted tap, and only once the existing keyboard detector confirms open.
    if (completedTap === document.activeElement && eligible(completedTap) &&
      document.documentElement.hasAttribute('data-keyboard-open')) {
      completedTap = null; stoppedByGesture = false;
    }
    if (stoppedByGesture || !eligible(document.activeElement)) return;
    editor = document.activeElement;
    if (frame) return;
    start = 0; lastY = scrollY;
    const v = keyboardViewport(); lastTop = v.top; lastBottom = v.bottom;
    quietSince = performance.now(); deadline = quietSince + 1400;
    frame = requestAnimationFrame(tick);
  };
  const focus = () => { cancel(); completedTap = null; stoppedByGesture = false; begin(); };
  const blur = () => { cancel(); editor = null; tap = null; completedTap = null; };
  const gesture = () => { stoppedByGesture = true; tap = null; completedTap = null; cancel(); };
  const down = (e: PointerEvent) => {
    gesture();
    if (e.isTrusted && e.isPrimary && e.button === 0 && e.target === document.activeElement &&
      eligible(document.activeElement)) {
      tap = { target: document.activeElement, id: e.pointerId, x: e.clientX, y: e.clientY, up: false };
    }
  };
  const move = (e: PointerEvent) => {
    if (tap && e.pointerId === tap.id && Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > 8) gesture();
  };
  const up = (e: PointerEvent) => {
    if (!tap) return;
    move(e);
    if (tap && e.isTrusted && e.pointerId === tap.id && e.target === tap.target) tap.up = true;
    else gesture();
  };
  const click = (e: MouseEvent) => {
    const finished = tap; tap = null;
    if (!e.isTrusted || !finished?.up || e.target !== finished.target ||
      document.activeElement !== finished.target || !eligible(finished.target)) return;
    completedTap = finished.target;
    cancel(); begin();
  };
  document.addEventListener('focusin', focus);
  document.addEventListener('focusout', blur);
  document.addEventListener('app-keyboard-geometry', begin);
  // Cancel throughout a drag. Only pointerup followed by a trusted click can
  // re-arm the same retained editor; neither pointerdown nor resize is intent.
  document.addEventListener('pointerdown', down, { passive: true });
  document.addEventListener('pointermove', move, { passive: true });
  document.addEventListener('pointerup', up, { passive: true });
  document.addEventListener('pointercancel', gesture, { passive: true });
  document.addEventListener('touchcancel', gesture, { passive: true });
  document.addEventListener('click', click, { passive: true });
  document.addEventListener('touchmove', gesture, { passive: true });
  document.addEventListener('wheel', gesture, { passive: true });
  window.addEventListener('pagehide', blur);
  return () => {
    blur();
    document.removeEventListener('focusin', focus);
    document.removeEventListener('focusout', blur);
    document.removeEventListener('app-keyboard-geometry', begin);
    document.removeEventListener('pointerdown', down);
    document.removeEventListener('pointermove', move);
    document.removeEventListener('pointerup', up);
    document.removeEventListener('pointercancel', gesture);
    document.removeEventListener('touchcancel', gesture);
    document.removeEventListener('click', click);
    document.removeEventListener('touchmove', gesture);
    document.removeEventListener('wheel', gesture);
    window.removeEventListener('pagehide', blur);
  };
}
